import { useState, useMemo } from 'react';
import { useAppStore } from '../store/useAppStore';
import { Minus, Plus, Loader2 } from 'lucide-react';
import { WalnutPageHeader } from './WalnutPageHeader';
import { getLocalImagePath } from '../utils/imageMapping';

export const WalnutList = () => {
  const walnuts = useAppStore(s => s.walnuts);
  const currentSave = useAppStore(s => s.saves.find(save => save.id === s.currentSaveId));
  const updateWalnutCount = useAppStore(s => s.updateWalnutCount);
  const user = useAppStore(s => s.user);
  
  // Search and filter functionality will be implemented in a future update
  const [searchQuery] = useState('');
  const [locationFilter] = useState('');
  const [loadingWalnuts, setLoadingWalnuts] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  // Filter walnuts based on search and location
  const filteredWalnuts = useMemo(() => {
    return walnuts.filter((walnut) => {
      const matchesSearch = searchQuery === '' || 
        walnut.type_of_discovery.toLowerCase().includes(searchQuery.toLowerCase()) ||
        walnut.location.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesLocation = !locationFilter || walnut.location === locationFilter;
      return matchesSearch && matchesLocation;
    });
  }, [walnuts, searchQuery, locationFilter]);

  const handleIncrement = async (walnutId: string) => {
    if (!currentSave || !user) return;
    
    const currentCount = currentSave.walnuts[walnutId] || 0;
    const maxCount = walnuts.find(w => w.id === walnutId)?.quantity || 0;
    
    if (currentCount < maxCount) {
      setLoadingWalnuts(prev => new Set(prev).add(walnutId));
      setError(null);
      
      try {
        console.log(`Incrementing walnut ${walnutId} for save ${currentSave.id}`);
        await updateWalnutCount(currentSave.id, walnutId, currentCount + 1);
        console.log(`Successfully incremented walnut ${walnutId}`);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update walnut count';
        console.error(`Failed to increment walnut ${walnutId}:`, {
          walnutId,
          saveId: currentSave.id,
          currentCount,
          targetCount: currentCount + 1,
          error: err,
        });
        setError(`Failed to increment walnut: ${errorMessage}`);
      } finally {
        setLoadingWalnuts(prev => {
          const newSet = new Set(prev);
          newSet.delete(walnutId);
          return newSet;
        });
      }
    }
  };

  const handleDecrement = async (walnutId: string) => {
    if (!currentSave || !user) return;
    
    const currentCount = currentSave.walnuts[walnutId] || 0;
    
    if (currentCount > 0) {
      setLoadingWalnuts(prev => new Set(prev).add(walnutId));
      setError(null);
      
      try {
        console.log(`Decrementing walnut ${walnutId} for save ${currentSave.id}`);
        await updateWalnutCount(currentSave.id, walnutId, currentCount - 1);
        console.log(`Successfully decremented walnut ${walnutId}`);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update walnut count';
        console.error(`Failed to decrement walnut ${walnutId}:`, {
          walnutId,
          saveId: currentSave.id,
          currentCount,
          targetCount: currentCount - 1,
          error: err,
        });
        setError(`Failed to decrement walnut: ${errorMessage}`);
      } finally {
        setLoadingWalnuts(prev => {
          const newSet = new Set(prev);
          newSet.delete(walnutId);
          return newSet;
        });
      }
    }
  };

  if (!user) {
    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
        <WalnutPageHeader />
        <div className="p-8 text-center text-gray-500">
          <p>Please sign in to track your golden walnuts</p>
        </div>
      </div>
    );
  }

  if (!currentSave) {
    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
        <WalnutPageHeader />
        <div className="p-8 text-center text-gray-500">
          <p>Please select a save file to track golden walnuts</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200">
      <WalnutPageHeader />
      
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}
      
      <div className="p-4">
        {filteredWalnuts.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No walnuts found matching your criteria</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredWalnuts.map((walnut) => {
              const isLoading = loadingWalnuts.has(walnut.id);
              const currentCount = currentSave?.walnuts[walnut.id] || 0;
              
              return (
                <div key={walnut.id} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg">
                  <div className="flex-shrink-0">
                    <img
                      src={getLocalImagePath(walnut.image)}
                      alt={walnut.type_of_discovery}
                      className="w-16 h-16 object-cover rounded-lg"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.src = '/images/placeholder.png';
                      }}
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {walnut.type_of_discovery}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">{walnut.location}</p>
                    <p className="text-xs text-gray-500 mt-1">{walnut.how_to_find}</p>
                  </div>
                  
                  <div className="flex items-center space-x-2.5 ml-4">
                    <button
                      onClick={() => handleDecrement(walnut.id)}
                      disabled={currentCount <= 0 || isLoading}
                      className={`p-1.5 rounded-full transition-colors ${
                        currentCount <= 0 || isLoading
                          ? 'text-gray-200 cursor-not-allowed' 
                          : 'text-red-500 hover:bg-red-50 hover:text-red-600'
                      }`}
                      aria-label="Decrease count"
                    >
                      {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Minus size={16} />}
                    </button>
                    
                    <div className="w-12 text-center flex flex-row items-center justify-center">
                      <span className="text-gray-900">
                        {currentCount}
                      </span>
                      <span className="text-gray-400 block">
                        /{walnut.quantity}
                      </span>
                    </div>
                    
                    <button
                      onClick={() => handleIncrement(walnut.id)}
                      disabled={currentCount >= walnut.quantity || isLoading}
                      className={`p-1.5 rounded-full transition-colors ${
                        currentCount >= walnut.quantity || isLoading
                          ? 'text-gray-200 cursor-not-allowed' 
                          : 'text-green-500 hover:bg-green-50 hover:text-green-600'
                      }`}
                      aria-label="Increase count"
                    >
                      {isLoading ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
